import type { UIMessage } from 'ai';
import type { ResumeStorage } from './mount-resume-storage.ts';
import { answerProgressStatus } from './answer-progress.ts';
import {
  answerFromStreamId,
  answerReadPath,
  answerStreamId,
} from '../../src/chat-progress/descriptor.mjs';

/** Availability from the actual mounted reader's /api/config response. This
 * never grants read or Stop rights; those stay current on every server call. */
export function answerReaderAdmitted(config: unknown): boolean {
  return (
    config !== null &&
    typeof config === 'object' &&
    (config as Record<string, unknown>).answerReader === true
  );
}

/** A reload may have an answer address before serving config has arrived.
 * Keep that address pending while admission is unknown, without delaying an
 * ordinary legacy resume or consuming the native SDK's one resume effect. */
export function admittedAnswerStorage(
  storage: ResumeStorage,
  admitted: () => boolean,
): ResumeStorage {
  return {
    ...storage,
    getStreamId: () => {
      const id = storage.getStreamId();
      return id?.startsWith('answer:') && admitted() !== true ? null : id;
    },
  };
}

/** Same original address, with the thread bound by this mounted pane. This
 * lookup still requires the server's current session/read/control authority. */
export function answerResumeApi(
  threadId: string,
  api: (path: string) => string,
  legacy: (streamId: string) => string,
  readerAdmitted = false,
) {
  return (streamId: string) => {
    if (!streamId.startsWith('answer:')) return legacy(streamId);
    if (readerAdmitted !== true) throw new Error('answer_reader_unavailable');
    const selected = answerFromStreamId(streamId);
    if (!selected || selected.threadId !== threadId) throw new Error('invalid_answer_resume');
    return api(answerReadPath(selected));
  };
}

/** A fresh device has no local resume ID. The authenticated history's actual
 * producing answer supplies a lookup only, never permission or a new Send. */
export function discoverAnswerAtMount(
  storage: ResumeStorage,
  threadId: string,
  messages: UIMessage[],
  readerAdmitted = false,
) {
  if (readerAdmitted !== true) return;
  const message = [...messages]
    .reverse()
    .find(
      (item) => item.role === 'assistant' && answerProgressStatus(item.metadata) === 'producing',
    );
  if (message) storage.adoptAtMount?.(answerStreamId({ threadId, answerId: message.id }));
}
